import { css } from '@emotion/react'
import { Text, VFlow } from 'bold-ui'
import { DateTime } from 'components/date/DateTime'
import { useFlags } from 'config/useFlagsContext'
import {
  FatoresRiscoPreEclampsiaModalPayload,
  useFatoresRiscoPreEclampsiaModal,
} from 'hooks/sdc/useFatoresRiscoPreEclampsiaModal'
import { calculateDpp } from 'util/date/calculateDpp'
import { ACOMPANHAMENTOS_PRE_NATAL_URL } from 'view/atendimentos/detail/acompanhamentos/getAcompanhamentosSideMenuItems'
import { INFO_GESTACAO_URL } from 'view/atendimentos/detail/acompanhamentos/pre-natal/AcompanhamentoPreNatalTabs'

import { RiscoPreEclampsiaTag } from '../../../pre-natal/components/RiscoPreEclampsiaTag'
import { SdcFatoresRiscoPreEclampsiaModal } from '../../../pre-natal/components/SdcFatoresRiscoPreEclampsiaModal'
import { PreNatalCardContentModel } from '../../../pre-natal/model-preNatal'
import { formatIdadeGestacional } from '../../../pre-natal/util-preNatal'
import { ContentCardSection } from '../../components/ContentCardSection'
import { VisualizarAcompanhamentoButton } from '../../components/VisualizarAcompanhamentoButton'
import { UsePeriodoGestacionalResult } from '../hooks/usePeriodoGestacional'
import { IdadeGestacionalAcompanhamentoPreNatal, IgStatus } from '../model-acompanhamentoPreNatal'
import { getIgStatus } from '../util/util-acompanhamentoPreNatal'
import { PreNatalCardItems } from './PreNatalCardItems'
import { TagGestacaoEncerrada } from './TagGestacaoEncerrada'

interface PreNatalCardProps {
  content: PreNatalCardContentModel
  loading: boolean
  dataUltimaConsultaOdontoGestante: LocalDate
  readOnly: boolean
  periodoGestacional: UsePeriodoGestacionalResult
  viaFolhaRosto: boolean
  acompanhamentosBasePath?: string
}

function renderDum(dataUltimaMenstruacao: Date, hasDumAtendimento: boolean) {
  return (
    <PreNatalCardItems<Date>
      label='DUM'
      value={dataUltimaMenstruacao}
      content={<DateTime format='DD/MM/YYYY' value={dataUltimaMenstruacao} />}
      isRegistradoAgora={hasDumAtendimento}
      dataTestId='PreNatalCard.dum'
    />
  )
}

function renderIdadeGestacional(idadeGestacional: IdadeGestacionalAcompanhamentoPreNatal, igStatus: IgStatus) {
  const { cronologicaEmDias, ecograficaEmDias } = idadeGestacional ?? {}
  const { hasIgChanged, isFonteIgEco } = igStatus

  const labelIg = `IG ${isFonteIgEco ? '(eco. obst.)' : '(DUM)'}`
  const fraseIgSemanasDias = formatIdadeGestacional(isFonteIgEco ? ecograficaEmDias : cronologicaEmDias)

  return (
    <PreNatalCardItems<string>
      label={labelIg}
      value={fraseIgSemanasDias}
      content={fraseIgSemanasDias}
      isRegistradoAgora={hasIgChanged}
      dataTestId='PreNatalCard.idadeGestacional'
    />
  )
}

function renderDpp(
  dataUltimaMenstruacao: Date,
  hasDumChanged: boolean,
  dataProvavelPartoEcografica: Date,
  hasDppEcoChanged: boolean,
  isFonteIgEco: boolean
) {
  const labelDpp = `DPP ${isFonteIgEco ? '(eco. obst.)' : '(DUM)'}`
  const dpp = isFonteIgEco ? dataProvavelPartoEcografica : calculateDpp(dataUltimaMenstruacao).dpp
  const hasDppChanged = isFonteIgEco ? hasDppEcoChanged : hasDumChanged

  return (
    <PreNatalCardItems<Date>
      label={labelDpp}
      value={dpp}
      content={<DateTime format='DD/MM/YYYY' value={dpp} />}
      isRegistradoAgora={hasDppChanged}
      dataTestId='PreNatalCard.dpp'
    />
  )
}

function renderTipoGravidez(tipoGravidez: string, hasTipoGravidezChanged: boolean) {
  return (
    <PreNatalCardItems<string>
      label='Tipo de gravidez'
      value={tipoGravidez}
      content={tipoGravidez}
      isRegistradoAgora={hasTipoGravidezChanged}
      dataTestId='PreNatalCard.tipoGravidez'
    />
  )
}

function renderRiscoPreEclampsiaSdc({
  isModalFatoresRiscoPreEclampsiaOpen,
  openModalFatoresRiscoPreEclampsia,
  closeModalFatoresRiscoPreEclampsia,
}: FatoresRiscoPreEclampsiaModalPayload) {
  return (
    <VFlow vSpacing={0.25} data-testid='PreNatalCard.riscoPreEclampsia'>
      <Text fontWeight='bold'>Risco de pré-eclâmpsia:</Text>
      <RiscoPreEclampsiaTag dataTestId='PreNatalCard.riscoPreEclampsiaTag' />
      <Text
        color='primary'
        fontWeight='normal'
        textDecoration='underline'
        style={classes.pointer}
        onClick={openModalFatoresRiscoPreEclampsia}
      >
        Ver fatores analisados
      </Text>
      <SdcFatoresRiscoPreEclampsiaModal
        modalOpen={isModalFatoresRiscoPreEclampsiaOpen}
        setModalOpen={closeModalFatoresRiscoPreEclampsia}
      />
    </VFlow>
  )
}

export function PreNatalCard(props: PreNatalCardProps) {
  const {
    dataUltimaConsultaOdontoGestante,
    content,
    loading,
    readOnly,
    periodoGestacional,
    viaFolhaRosto,
    acompanhamentosBasePath,
  } = props

  const { idadeGestacional, dataProvavelPartoEcografica, hasIgEcoChanged, hasDppEcoChanged } = periodoGestacional
  const {
    dumStatus: { dumPrioritaria, hasDumChanged },
    quantidadeConsultas,
    dataUltimaConsulta,
    tipoGravidez,
    hasTipoGravidezChanged,
  } = content

  const { PILOTO_SDC_PRE_ECLAMPSIA_ENABLED } = useFlags()

  const igStatus = getIgStatus(idadeGestacional, hasIgEcoChanged, hasDumChanged)

  const modalPayload = useFatoresRiscoPreEclampsiaModal()

  return (
    <ContentCardSection
      isLoading={loading}
      messageLoading='Carregando dados do pré-natal'
      dataTestId='PreNatalCard.container'
    >
      <VFlow vSpacing={0.5}>
        {!readOnly && <TagGestacaoEncerrada dataTestId='PreNatalCard.tagEncerramento' />}
        {renderDum(dumPrioritaria, hasDumChanged)}
        {renderIdadeGestacional(idadeGestacional, igStatus)}
        {renderDpp(dumPrioritaria, hasDumChanged, dataProvavelPartoEcografica, hasDppEcoChanged, igStatus.isFonteIgEco)}
        {renderTipoGravidez(tipoGravidez, hasTipoGravidezChanged)}
        {PILOTO_SDC_PRE_ECLAMPSIA_ENABLED && !viaFolhaRosto && renderRiscoPreEclampsiaSdc(modalPayload)}
        <Text data-testid='PreNatalCard.contadorConsultas'>
          <strong>Consultas: </strong>
          {quantidadeConsultas + ' | Última em '}
          <DateTime format='DD/MM/YYYY' value={dataUltimaConsulta} />
        </Text>
        <VFlow vSpacing={0} data-testid='PreNatalCard.consultaOdontologica'>
          <Text fontWeight='bold'>Última consulta odontológica:</Text>
          {dataUltimaConsultaOdontoGestante ? (
            <DateTime format='DD/MM/YYYY' value={dataUltimaConsultaOdontoGestante} />
          ) : (
            <Text>Consulta não realizada</Text>
          )}
        </VFlow>
        <VisualizarAcompanhamentoButton
          basePath={acompanhamentosBasePath}
          path={`${ACOMPANHAMENTOS_PRE_NATAL_URL}/${INFO_GESTACAO_URL}?redirect=prenatal`}
        />
      </VFlow>
    </ContentCardSection>
  )
}

const classes = {
  pointer: css`
    cursor: pointer;
  `,
}
